import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { RefreshCw, Plug, Clock, ChevronDown } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { formatDistanceToNow } from "date-fns";
import { useLocalization } from '@/hooks/useLocalization';

type Frequency = "manual" | "hourly" | "daily" | "weekly";

interface IntegrationConfig {
  id: string;
  name: string;
  integration_type: string;
  sync_frequency: Frequency;
  last_sync_at: string | null;
  is_active: boolean;
}

const FREQ_OPTIONS: { value: Frequency; label: string }[] = [
  { value: "manual", label: "Manual only" },
  { value: "hourly", label: "Every hour" },
  { value: "daily", label: "Once a day" },
  { value: "weekly", label: "Once a week" },
];

const SyncControlPanel = () => {
  const { tx, t } = useLocalization();
  const [configs, setConfigs] = useState<IntegrationConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [syncingId, setSyncingId] = useState<string | null>(null);
  const [syncingAll, setSyncingAll] = useState(false);

  const load = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("integration_configs")
      .select("id,name,integration_type,sync_frequency,last_sync_at,is_active")
      .order("name");
    if (error) toast.error(error.message);
    else setConfigs((data ?? []) as IntegrationConfig[]);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const updateFrequency = async (id: string, freq: Frequency) => {
    const { error } = await supabase
      .from("integration_configs")
      .update({ sync_frequency: freq })
      .eq("id", id);
    if (error) return toast.error(error.message);
    setConfigs((c) => c.map((x) => (x.id === id ? { ...x, sync_frequency: freq } : x)));
    toast.success(t('Sync frequency updated to {0}', { 0: freq }));
  };

  const [expanded, setExpanded] = useState(false);

  const syncOne = async (id: string) => {
    setSyncingId(id);
    try {
      const { data, error } = await supabase.functions.invoke("run-scheduled-syncs", {
        body: { integration_id: id, force: true },
      });
      if (error) throw error;
      const result = (data as { results?: Array<{ ok?: boolean; error?: string }> })?.results?.[0];
      if (result?.ok) toast.success(t('Sync complete'));
      else toast.error(result?.error ?? t('Sync failed'));
      await load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : t('Sync failed'));
    } finally {
      setSyncingId(null);
    }
  };

  const syncAll = async () => {
    setSyncingAll(true);
    try {
      const { data, error } = await supabase.functions.invoke("run-scheduled-syncs", {
        body: { force: true },
      });
      if (error) throw error;
      const ran = (data as { ran?: number })?.ran ?? 0;
      toast.success(t('Triggered sync for {0} integration{1}', { 0: ran, 1: ran === 1 ? "" : "s" }));
      await load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : t('Sync failed'));
    } finally {
      setSyncingAll(false);
    }
  };

  const staleCount = configs.filter(
    (c) => !c.last_sync_at || Date.now() - new Date(c.last_sync_at).getTime() > 3 * 24 * 3600 * 1000,
  ).length;
  const newest = configs.reduce<string | null>(
    (acc, c) => (c.last_sync_at && (!acc || c.last_sync_at > acc) ? c.last_sync_at : acc),
    null,
  );

  return (
    <Card className="px-4 py-3">
      <div className="flex items-center justify-between gap-3">
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
          className="flex min-w-0 flex-1 items-center gap-2 text-start rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <Plug className="h-4 w-4 shrink-0 text-muted-foreground" />
          <span className="text-sm font-medium text-foreground">{t('Data sources')}</span>
          <span className="truncate text-xs text-muted-foreground">
            {configs.length}  {t('connected')}
            {staleCount > 0 ? t(' · {0} need attention', { 0: staleCount }) : t(' · all up to date')}
            {newest ? t(' · last sync {0}', { 0: formatDistanceToNow(new Date(newest), { addSuffix: true }) }) : ""}
          </span>
          <ChevronDown className={`ms-auto h-4 w-4 shrink-0 text-muted-foreground transition-transform ${expanded ? "rotate-180" : ""}`} />
        </button>
        <Button size="sm" variant="outline" onClick={syncAll} disabled={syncingAll || configs.length === 0}>
          <RefreshCw className={`h-4 w-4 me-2 ${syncingAll ? "animate-spin" : ""}`} />
          
          {t('Sync all')}
        </Button>
      </div>

      {expanded && (
        <div className="mt-3">
      {loading ? (
        <p className="text-sm text-muted-foreground">{t('Loading integrations…')}</p>
      ) : configs.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          
          {t('No integrations configured yet. Connect Microsoft 365, Jira, or other sources from the Integrations page.')}
        </p>
      ) : (
        <ul className="divide-y divide-border rounded-lg border border-border overflow-hidden">
          {configs.map((cfg) => {
            const stale = !cfg.last_sync_at || Date.now() - new Date(cfg.last_sync_at).getTime() > 3 * 24 * 3600 * 1000;
            return (
              <li
                key={cfg.id}
                className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 px-4 py-3 bg-card hover:bg-muted/40 transition-colors"
              >
                <div className="min-w-0 flex items-start gap-3">
                  <span
                    className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${stale ? "bg-amber-500" : "bg-emerald-500"}`}
                    aria-label={stale ? t('Sync is stale') : t('Sync is healthy')}
                  />
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-foreground truncate">{cfg.name}</p>
                    <p className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
                      <Clock className="h-3 w-3" />
                      {cfg.last_sync_at
                        ? t('Synced {0}', { 0: formatDistanceToNow(new Date(cfg.last_sync_at), { addSuffix: true }) })
                        : t('Never synced')}
                      <span aria-hidden="true">·</span>
                      <span className="truncate">{cfg.integration_type.replace(/_/g, " ")}</span>
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2 ps-5 sm:ps-0">
                  <Select
                    value={cfg.sync_frequency}
                    onValueChange={(v) => updateFrequency(cfg.id, v as Frequency)}
                  >
                    <SelectTrigger className="w-[140px] h-9 text-sm" aria-label={t('Sync frequency for {0}', { 0: cfg.name })}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {FREQ_OPTIONS.map((o) => (
                        <SelectItem key={o.value} value={o.value}>{tx(o.label)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-9 w-9"
                    aria-label={t('Sync {0} now', { 0: cfg.name })}
                    onClick={() => syncOne(cfg.id)}
                    disabled={syncingId === cfg.id}
                  >
                    <RefreshCw className={`h-4 w-4 ${syncingId === cfg.id ? "animate-spin" : ""}`} />
                  </Button>
                </div>
              </li>
            );
          })}
        </ul>
      )}
        </div>
      )}
    </Card>
  );
};

export default SyncControlPanel;
